<!--SelectCustomV2-->
<template>
  <div class="custom-select-container relative w-full" :class="[customClassPadre, { 'is-disabled': isDisabled }]"
    ref="selectContainer">
    <!-- LOADER (usa el MISMO contenedor visual, pero sin duplicar) -->
    <div v-if="loading" class="custom-select-trigger flex h-full w-full bg-BLANCO_LEON items-center rounded-md gap-1"
      :class="customClass" aria-busy="true">
      <span class="flex-1 flex items-center justify-center">
        <LoaderSvg strokeColor="#BD2025" sizeClass="w-4 h-4" />
      </span>

      <!-- slot derecho para mantener alineación -->
      <span class="icon-right flex items-center justify-center w-auto shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#6B7280" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </span>
    </div>

    <!-- TRIGGER REAL (solo cuando NO está loading) -->
    <div v-else class="custom-select-trigger flex h-full w-full bg-BLANCO_LEON items-center rounded-md gap-1"
      :class="[customClass, isOpen ? 'is-open' : '']" @click="toggleDropdown" tabindex="0"
      @keydown.enter="toggleDropdown" @keydown.space.prevent="toggleDropdown">
      
      <span v-if="showSearchIcon && !(showOptionImage && selectedImage)" class="w-6 slot-left flex items-center justify-center shrink-0 overflow-hidden">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#6B7280" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
        </svg>
      </span>

      <span class="selected-value">
        <img v-if="showOptionImage && selectedImage" :src="selectedImage" :class="['object-contain', optionImageClass]"
          alt="logo" />
        <span class="selected-text">{{ displayValue }}</span>
      </span>

      <span class="icon-right flex items-center justify-center w-auto shrink-0" :class="isOpen ? 'is-open' : ''">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#6B7280" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </span>
    </div>

    <!-- Opciones del dropdown para desktop -->
    <div v-if="isOpen && !isMobile"
      class="custom-select-dropdown fade-in absolute z-[90] left-0 w-full bg-BLANCO_LEON overflow-y-auto rounded-md"
      ref="dropdown">
      <!-- Opciones dinámicas -->
      <template v-if="normalizedOptions.length > 0">
        <div v-for="(option, index) in normalizedOptions" :key="option.value" class="custom-select-option" :class="{
          'is-selected': currentValue === option.value,
          'is-active': activeIndex === index
        }" @click.stop="selectOption(option.value)" @keydown.enter="selectOption(option.value)"
          @keydown.up.prevent="navigateOptions(-1)" @keydown.down.prevent="navigateOptions(1)"
          @keydown.space.prevent="toggleDropdown" @keydown.esc="closeDropdown" tabindex="0"
          @mouseover="activeIndex = index">
          <div class="flex items-center justify-center gap-2">
            <img v-if="showOptionImage && option.image" :src="option.image"
              :class="['object-contain', optionImageClass]" alt="logo" />
            <span>{{ option.label }}</span>
          </div>

        </div>
      </template>
      <div v-else class="custom-select-option text-GRIS_CLARO_COMPLEMENTARIO italic cursor-default">No hay opciones
        disponibles</div>
    </div>

    <!-- Modal de opciones para móvil usando Teleport -->
    <Teleport to="body" v-if="isOpen && isMobile">
      <div class="mobile-select-modal fixed inset-0 flex items-end z-[9999]" @click="closeDropdown">
        <div
          class="mobile-select-content bg-gray-100 w-full max-h-[35vh] overflow-hidden slide-up-animation flex flex-col"
          @click.stop>
          <!-- Header fijo -->
          <div
            class="mobile-select-header bg-BLANCO_LEON flex justify-between items-center px-4 py-3 border-b flex-shrink-0">
            <div class="flex flex-row gap-2 items-center">
              <img :src="getModalIcon" alt="" class="h-6 w-6" />
              <h3 class="text-lg font-semibold">{{ texto }}</h3>
            </div>

            <button @click="closeDropdown"
              class="flex justify-center items-center p-1 hover:bg-gray-100 rounded-full transition-colors">
              <img src="/img/chat/cerrar.png" alt="Cerrar" class="h-6 w-6 object-fill" loading="lazy" />
            </button>
          </div>

          <!-- Contenedor scrolleable de opciones -->
          <div class="flex-1 overflow-y-auto">
            <div class="p-4 space-y-3">
              <!-- Opciones dinámicas -->
              <template v-if="normalizedOptions.length > 0">
                <div v-for="(option, index) in normalizedOptions" :key="option.value"
                  class="mobile-select-option bg-BLANCO_LEON py-3 px-4 rounded-lg border border-gray-200 transition-all duration-200"
                  :class="{
                    'is-selected': currentValue === option.value,
                    'hover:bg-gray-50': currentValue !== option.value
                  }" @click="selectOption(option.value)">
                  <div class="flex items-center justify-center gap-2">
                    <img v-if="showOptionImage && option.image" :src="option.image"
                      :class="['object-contain', optionImageClass]" alt="logo" />
                    <span class="text-base">{{ option.label }}</span>
                  </div>
                </div>
              </template>
              <div v-else class="text-center py-12 px-4">
                <span class="text-GRIS_CLARO_COMPLEMENTARIO italic text-base">No hay opciones disponibles</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import LoaderSvg from "~/components/Ui/LoaderSvg.vue";


// Props
const props = defineProps({
  //  NUEVA: Prop value para uso con :value
  value: {
    type: [String, Number],
    default: ''
  },
  // Mantener modelValue para retrocompatibilidad con v-model
  modelValue: {
    type: [String, Number],
    default: ''
  },
  texto: {
    type: String,
    default: ''
  },
  customClassPadre: {
    type: String,
    default: ''
  },
  customClass: {
    type: String,
    default: ''
  },
  opciones: {
    type: Array,
    default: () => []
  },
  disabled: {
    type: Boolean,
    default: false
  },
  imagen_modal: {
    type: String,
    default: ''
  },
  showSearchIcon: {
    type: Boolean,
    default: true
  },
  manageBodyScroll: {
    type: Boolean,
    default: true
  },
  showOptionImage: {
    type: Boolean,
    default: false
  },
  optionImageKey: {
    type: String,
    default: 'logo'
  },
  optionImageClass: {
    type: String,
    default: 'h-6 w-6'
  },
  loading: {
    type: Boolean,
    default: false
  },

})

// Emits
const emit = defineEmits(['update:modelValue', 'changeValue'])

// Reactive references
const isOpen = ref(false)
const dropdown = ref(null)
const activeIndex = ref(-1)
const selectContainer = ref(null)
const isMobile = ref(false)

//  CAMBIO PRINCIPAL: Determinar valor actual (priorizar 'value' sobre 'modelValue')
const currentValue = computed(() => {
  // Si se proporciona 'value', usar eso (para :value + @changeValue)
  // Si no, usar 'modelValue' (para v-model)
  return props.value !== '' ? props.value : props.modelValue;
})

// Detectar si es dispositivo móvil
const checkIfMobile = () => {
  isMobile.value = window.innerWidth < 768
}

const getModalIcon = computed(() => {
  let iconMap = `/img/dc_img/plp_redisenio/${props.imagen_modal}.svg`
  return iconMap;
})

// Normalizar las opciones para tener un formato consistente { value, label }
const normalizedOptions = computed(() => {
  if (!props.opciones || props.opciones.length === 0) return [];

  const getImg = (item) => {
    if (!props.showOptionImage) return '';
    return (
      item?.[props.optionImageKey] ??
      item?.logo ??
      item?.imagen ??
      item?.image ??
      item?.icon ??
      ''
    );
  };

  if (props.opciones[0] && typeof props.opciones[0] === 'object' && props.opciones[0].hasOwnProperty('id')) {
    return props.opciones.map(item => ({
      value: item.id,
      label: item.nombre,
      image: getImg(item),
    }));
  } else {
    return props.opciones.map(item => ({
      value: item,
      label: item,
      image: '',
    }));
  }
});

// Auto-disable cuando hay una sola opción o menos
const shouldDisable = computed(() => {
  const realOptionsCount = normalizedOptions.value.length
  return realOptionsCount < 2
})

// Estado final de disabled
const isDisabled = computed(() => {
  return props.disabled || shouldDisable.value
})

//  CAMBIO: Usar currentValue en lugar de modelValue directamente
const displayValue = computed(() => {
  if (currentValue.value === '') {
    return props.texto
  }

  const selectedOption = normalizedOptions.value.find(opt => opt.value === currentValue.value)
  return selectedOption ? selectedOption.label : props.texto
})

const selectedOptionObj = computed(() => {
  return normalizedOptions.value.find(opt => opt.value === currentValue.value) || null;
});

const selectedImage = computed(() => {
  return selectedOptionObj.value?.image || '';
});

// Controlar clics fuera del componente
const handleOutsideClick = (event) => {
  if (!selectContainer.value) return

  if (isOpen.value && !selectContainer.value.contains(event.target) && !isMobile.value) {
    isOpen.value = false
  }
}

// Alternar el estado del dropdown
const toggleDropdown = () => {
  if (isDisabled.value) return

  isOpen.value = !isOpen.value

  // AGREGAR manageBodyScroll también al abrir
  if (isOpen.value && isMobile.value && props.manageBodyScroll) {
    document.body.classList.add('popup-active')
  } else if (!isOpen.value && isMobile.value && props.manageBodyScroll) {
    document.body.classList.remove('popup-active')
  }

  if (isOpen.value && !isMobile.value) {
    nextTick(() => {
      const selectedIndex = normalizedOptions.value.findIndex(opt => opt.value === currentValue.value)
      activeIndex.value = selectedIndex >= 0 ? selectedIndex : 0

      const options = dropdown.value?.querySelectorAll('.custom-select-option')
      if (options && options[activeIndex.value]) {
        options[activeIndex.value].focus()
      }
    })
  }
}

const closeDropdown = () => {
  if (isMobile.value) {
    nextTick(() => {
      const modalElement = document.querySelector('.mobile-select-modal')
      const contentElement = document.querySelector('.mobile-select-content')

      if (modalElement && contentElement) {
        contentElement.classList.add('slide-down-animation')
        modalElement.classList.add('fade-out')

        setTimeout(() => {
          isOpen.value = false
          if (props.manageBodyScroll) {
            document.body.classList.remove('popup-active')
          }
        }, 280)
      } else {
        isOpen.value = false
        // CAMBIAR ESTA LÍNEA:
        if (props.manageBodyScroll) {
          document.body.classList.remove('popup-active')
        }
      }
    })
  } else {
    isOpen.value = false
  }
}

// Navegar por las opciones con el teclado
const navigateOptions = (direction) => {
  if (!isOpen.value) {
    isOpen.value = true
    return
  }

  const optionsCount = normalizedOptions.value.length

  if (direction > 0) {
    activeIndex.value = (activeIndex.value + 1) % optionsCount
  } else {
    activeIndex.value = (activeIndex.value - 1 + optionsCount) % optionsCount
  }

  nextTick(() => {
    const options = dropdown.value?.querySelectorAll('.custom-select-option')
    if (options && options[activeIndex.value]) {
      options[activeIndex.value].focus()
    }
  })
}

//  MANTENER: Doble emisión para máxima compatibilidad
const selectOption = (value) => {
  emit('update:modelValue', value)  // Para v-model
  emit('changeValue', value)        // Para :value + @changeValue
  isOpen.value = false
  if (isMobile.value && props.manageBodyScroll) {
    document.body.classList.remove('popup-active')
  }
}

// Lifecycle hooks
onMounted(() => {
  checkIfMobile()
  window.addEventListener('resize', checkIfMobile)
  document.addEventListener('click', handleOutsideClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick)
  window.removeEventListener('resize', checkIfMobile)
})

</script>

<style scoped>

.custom-select-container {
  user-select: none;
}

.custom-select-trigger {
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.custom-select-trigger:focus {
  outline: none;
  border-color: #4299e1;
  box-shadow: 0 0 0 1px rgba(66, 153, 225, 0.5);
}

.custom-select-trigger.is-open {
  border-color: #4299e1;
}

.selected-value {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.selected-value img {
  flex-shrink: 0;
}

.selected-text {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.icon-right {
  transition: transform 0.2s ease;
}

.icon-right.is-open {
  transform: rotate(-180deg);
}

.custom-select-dropdown {
  top: calc(100% + 4px);
  max-height: 250px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.custom-select-option {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: background-color 0.1s ease;
  text-align: center;
}

.custom-select-option:hover,
.custom-select-option.is-active {
  background-color: #f7fafc;
}

.custom-select-option.is-selected {
  background-color: #ebf8ff;
  color: #2b6cb0;
  font-weight: 500;
}

.custom-select-option:focus {
  outline: none;
  background-color: #f0f4f8;
}

.fade-in {
  animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.is-disabled .custom-select-trigger {
  background-color: #f7fafc;
  border-color: #edf2f7;
  color: #a0aec0;
  cursor: not-allowed;
}

.mobile-select-modal {
  background-color: rgba(0, 0, 0, 0.5);
  animation: fadeIn 0.25s ease-in-out;
  z-index: 9999 !important;
}

.mobile-select-content {
  border-radius: 1rem 1rem 0 0;
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
  transform: translateY(100%);
  animation: slideUp 0.3s ease-out forwards;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
}

@keyframes slideDown {
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(100%);
  }
}

.slide-down-animation {
  animation: slideDown 0.3s ease-in forwards;
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

.fade-out {
  animation: fadeOut 0.25s ease-in-out forwards;
}

.mobile-select-header {
  border-bottom: 1px solid #e2e8f0;
}

.mobile-select-option {
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.mobile-select-option.is-selected {
  background-color: #0f9a34 !important;
  color: #ffffff;
  font-weight: 500;
  border-color: #0f9a34;
}

:global(body.popup-active) {
  overflow: hidden !important;
}

.custom-select-trigger {
  text-align: center;
}
</style>